import { Link } from 'react-router-dom';

/**
 * DeprecatedBanner — the loud notice atop every page that is kept only as a
 * record. Mounted at the route level in App.tsx so legacy pages need no edits
 * of their own. Owner ruling 2026-10-01: legacy material is called out in the
 * header of every document, not with a quiet strip. Pages stay reachable (the
 * owner's delta requirement); this bar tells the truth about them.
 *
 * `to`/`label` name the current standard that replaces the page. `still`
 * says, where it is true, what still depends on the legacy thing.
 * Registry: the Legacy section of /elevation/design-language.
 */
export function DeprecatedBanner({
  to,
  label,
  external,
  still,
}: {
  to: string;
  label: string;
  external?: boolean;
  still?: string;
}) {
  const linkCls = 'font-medium underline decoration-[var(--p-clay)] underline-offset-4 transition-colors hover:text-[var(--p-clay)]';
  const link = external ? (
    <a href={to} className={linkCls}>{label} →</a>
  ) : (
    <Link to={to} className={linkCls}>{label} →</Link>
  );
  return (
    <div
      role="note"
      aria-label="Deprecated"
      className="daylight border-y border-l-[6px] bg-[#F7ECE7] px-5 py-4"
      // Inline: `.dark * { border-color }` in index.css comes after Tailwind's plain classes and would grey one.
      style={{ borderColor: '#E3D2CB', borderLeftColor: '#A85443' }}
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-1.5">
        <p className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="bg-[#A85443] px-2.5 py-1 font-mono text-[14px] font-bold uppercase tracking-[0.2em] text-[#FAFAF7]">Deprecated</span>
          <span className="font-serif text-[20px] leading-snug text-[#1E2A44]">Do not use this for new work.</span>
        </p>
        <p className="font-body text-[15px] leading-relaxed text-[#3E4A63]">
          This page is kept only as a record. {still ? `${still} ` : ''}The current standard is {link}
        </p>
      </div>
    </div>
  );
}
